import {IFormulaNode} from '@mintdata-wsp/common/controllers/spreadsheet/formula/ast/FormulaDefinitions';
import {EvalFormulaResult} from '@mintdata-wsp/common/controllers/spreadsheet/formula/engine/EvalFormulaResult';
import {FormulaGrammar} from '@mintdata-wsp/common/controllers/spreadsheet/formula/engine/FormulaGrammar';
import {FormulaOps} from '@mintdata-wsp/common/controllers/spreadsheet/formula/engine/FormulaOps';
import {FormulaType} from '@mintdata-wsp/common/controllers/spreadsheet/formula/engine/FormulaType';
import {OhmEngine} from '@mintdata-wsp/common/controllers/spreadsheet/formula/engine/OhmEngine';
import {
    OhmParseInputResult,
    OhmParseResult
} from '@mintdata-wsp/common/controllers/spreadsheet/formula/engine/OhmParseResult';
import {OhmUtils} from '@mintdata-wsp/common/controllers/spreadsheet/formula/engine/OhmUtils';
import {IEvalInputOpContext} from '@mintdata-wsp/common/controllers/spreadsheet/formula/FormulaInputEvalOp';
import {IFormulaParseContext} from '@mintdata-wsp/common/controllers/spreadsheet/formula/operations/buildAST/BuildFormulaAST';
import {ICellIOContext} from '@mintdata-wsp/common/controllers/spreadsheet/formula/operations/eval/CellIOContext';
import {EvalFormula} from '@mintdata-wsp/common/controllers/spreadsheet/formula/operations/eval/EvalFormula';
import {EvalStack} from '@mintdata-wsp/common/controllers/spreadsheet/formula/operations/eval/EvalStack';
import {IFunctionResolver} from '@mintdata-wsp/common/controllers/spreadsheet/formula/operations/eval/FunctionResolver';
import {ISheetIOContext} from '@mintdata-wsp/common/controllers/spreadsheet/formula/operations/eval/SheetIOContext';
import {FindAllCellReferences} from '@mintdata-wsp/common/controllers/spreadsheet/formula/operations/find/FindAllCellReferences';
import {PrintFormulaAST} from '@mintdata-wsp/common/controllers/spreadsheet/formula/operations/printAST/PrintFormulaAST';
import {SlotFormula} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/SlotTypes';
import {SlotGlobalKey, SlotLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SlotLocation';
import {ISurfaceContext, NamedArguments} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {area, mk} from '@mintdata/mint-kit/api/log/mk';
import bind from 'bind-decorator';

export class FormulaEngine
{
    private _ohmEval_Engine:OhmEngine = null;

    private constructor()
    {
    }

    public destroy():void
    {
    }

    private _getOhmEvalEngine():OhmEngine
    {
        if ( !this._ohmEval_Engine )
            this._ohmEval_Engine = OhmUtils.createEngine(FormulaGrammar.GrammarContent, [FormulaOps.BuildFormulaAST, FormulaOps.EvalInput]);

        return this._ohmEval_Engine;
    }

    @bind
    public printAST(parseResult:OhmParseResult):void
    {
        if( !parseResult.isParseSuccessful )
        {
            mk.error(area.Formula, 'printAST(): parse not successful', parseResult);
            return;
        }

        const visitor = new PrintFormulaAST();
        const rootNode:IFormulaNode = parseResult.rootNode;
        rootNode.accept(visitor);

        const printResult = visitor.getResult();
        mk.info(area.FormulaAST, '>>>>>>>>> Formula AST:\n' + printResult);
    }

    public getAllDependencies(rootNode:IFormulaNode):Set<SlotGlobalKey>
    {
        const visitor:FindAllCellReferences = new FindAllCellReferences();

        rootNode.accept(visitor);

        return visitor.getAllDependencies();
    }

    @bind
    public async evalFormula(rootNode:IFormulaNode,
                             sheetIO:ISheetIOContext,
                             cellIO:ICellIOContext,
                             functionResolver:IFunctionResolver,
                             surfaceContext:ISurfaceContext,
                             namedArguments?:NamedArguments,
                             javaEvalContext?:any):Promise<EvalFormulaResult>
    {
        const evalStack = new EvalStack();
        return this.evalFormulaWithContext(rootNode, evalStack, sheetIO, cellIO, functionResolver, surfaceContext, namedArguments, javaEvalContext);
    }

    @bind
    public async evalFormulaWithContext(rootNode:IFormulaNode,
                                        evalStack:EvalStack,
                                        sheetIO:ISheetIOContext,
                                        cellIO:ICellIOContext,
                                        functionResolver:IFunctionResolver,
                                        surfaceContext:ISurfaceContext,
                                        namedArguments?:NamedArguments,
                                        javaEvalContext?:any):Promise<EvalFormulaResult>
    {
        const visitor = new EvalFormula(evalStack, sheetIO, cellIO, functionResolver, surfaceContext, namedArguments, javaEvalContext);

        // Await evaluation result (async)
        await rootNode.acceptAsync(visitor);

        // Single stack item is result of evaluation
        const evalResult = visitor.getEvaluationResult();

        return new EvalFormulaResult(
            evalResult,
            rootNode,
            cellIO.getDependencies(),
            cellIO
        );
    }

    @bind
    public parseInputRC(formula:SlotFormula, location:SlotLocation):OhmParseInputResult
    {
        return this._parseInput(formula, location, 'formulaInputRC');
    }

    @bind
    public parseInputA1(formula:SlotFormula, location:SlotLocation):OhmParseInputResult
    {
        return this._parseInput(formula, location, 'formulaInputA1');
    }

    @bind
    private _parseInput(formula:SlotFormula, location:SlotLocation, startActionName:string):OhmParseInputResult
    {
        const currentColumnIndex = location.colIndex;
        const currentRowIndex = location.rowIndex;

        const parseContext:IEvalInputOpContext = {
            currentColumnIndex,
            currentRowIndex
        };

        const parseInputResult = this._getOhmEvalEngine().parseInputFormula(parseContext, formula as string, startActionName);

        if( !parseInputResult.isParseSuccessful )
            mk.error(area.Formula, `${parseInputResult.errorMessage}`);
        else
            mk.info(area.Formula, `INPUT EVALUATED TO: ${JSON.stringify(parseInputResult.result)}`);

        return parseInputResult;
    }

    @bind
    public parseRC(parseContext:IFormulaParseContext, formula:SlotFormula, isSilent:boolean = false):OhmParseResult
    {
        return this._parse(parseContext, formula, 'FormulaValueRC', isSilent);
    }

    @bind
    public parseA1(parseContext:IFormulaParseContext, formula:SlotFormula):OhmParseResult
    {
        return this._parse(parseContext, formula, 'FormulaValueA1');
    }

    @bind
    public parseExpr(parseContext:IFormulaParseContext,
                     formula:SlotFormula):OhmParseResult
    {
        return this._parse(parseContext, formula, 'BareExpression');
    }

    @bind
    private _parse(parseContext:IFormulaParseContext, formula:SlotFormula, startActionName:string, isSilent:boolean = false):OhmParseResult
    {
        mk.info(area.Formula, `FormulaEngine.parse(formula='${formula}', col=${parseContext.colIndex}, row=${parseContext.rowIndex})`);

        let ohmResult;

        if( typeof formula === 'string' )
        {
            try
            {
                ohmResult = this._getOhmEvalEngine().parseFormula(parseContext, formula, startActionName);
            }
            catch( error )
            {
                if( OhmUtils.isCircularRefException(error) )
                {
                    if( !isSilent )
                        mk.debug(area.Formula, 'error in Slot._parseFormula()', error);

                    return new OhmParseResult(false, null, FormulaType.RegularFormula, error);
                }
                else
                    throw error;
            }
        }
        else
            return new OhmParseResult(false, null, FormulaType.RegularFormula, 'IValue in formula');

        if( !ohmResult.isParseSuccessful && !isSilent )
            mk.error(area.Formula, `'${formula}' => ${ohmResult.errorMessage}`);
        else
            mk.info(area.Formula, `Parsed ${new SlotLocation(parseContext.colIndex, parseContext.rowIndex).toA1String()}`, ohmResult.rootNode);

        return ohmResult;
    }

    private static _SINGLETON:FormulaEngine = new FormulaEngine();

    public static getInstance():FormulaEngine
    {
        return FormulaEngine._SINGLETON;
    }
}
